import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Pencil, Trash2, Inbox, Lock, PackageOpen } from "lucide-react";
import OfferComparator from "@/components/marketplace/OfferComparator";
import PanelLayout from "@/components/layout/PanelLayout";
import { COUNTRIES as PILOT_COUNTRY_LIST, countryLabel } from "@/constants/countries";

/**
 * A20-P9 — Private marketplace requests.
 *
 * Path: /app/marketplace-requests
 *
 * Pure private-draft surface. Requests are born from wishlist items
 * (see MyWishlists.jsx for the "Prepare request" CTA). No vendor UI,
 * no offer UI, no sponsor UI. Status enum is the P9 subset only.
 */
const P9_STATUSES = ["draft", "ready_for_review", "internal_review", "cancelled", "archived"];
const PILOT_COUNTRIES = PILOT_COUNTRY_LIST.map((c) => c.code);

const fmtEur = (n) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `€${Math.round(Number(n) || 0)}`; }
};

const emptyForm = () => ({
  title: "", description: "", country: "ES", city: "",
  quantity: 1, max_budget: "", status: "draft",
  required_by_date: "", cancellation_reason: "",
});

const STATUS_PILL = {
  draft:             "bg-slate-100 text-slate-800",
  ready_for_review:  "bg-amber-100 text-amber-900",
  internal_review:   "bg-sky-100 text-sky-900",
  approved_internal: "bg-emerald-100 text-emerald-900",
  offer_selected:    "bg-emerald-200 text-emerald-900",
  rejected:          "bg-rose-100 text-rose-900",
  cancelled:         "bg-rose-100 text-rose-900",
  archived:          "bg-slate-200 text-slate-600",
};

const MyMarketplaceRequests = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  // A20-P12 — owner offers drawer state. Lazy-fetched on open.
  const [offersDrawer, setOffersDrawer] = useState({
    open: false, requestId: null, loading: false, data: null,
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketplace-requests");
      setRows(r.data?.requests || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("marketplace.toasts.saveFailed")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEdit = (req) => {
    setEditingId(req.id);
    setForm({
      title: req.title || "",
      description: req.description || "",
      country: req.country || "ES",
      city: req.city || "",
      quantity: req.quantity || 1,
      max_budget: req.max_budget ?? "",
      status: req.status || "draft",
      required_by_date: req.required_by_date || "",
      cancellation_reason: req.cancellation_reason || "",
    });
    setDrawerOpen(true);
  };

  const submit = async () => {
    if (!editingId) return;
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      country: form.country,
      city: form.city.trim() || null,
      quantity: Math.max(1, Number(form.quantity) || 1),
      max_budget: Number(form.max_budget) || 0,
      status: form.status,
      required_by_date: form.required_by_date || null,
    };
    if (form.status === "cancelled" && form.cancellation_reason.trim()) {
      payload.cancellation_reason = form.cancellation_reason.trim();
    }
    try {
      await api.put(`/marketplace-requests/${editingId}`, payload);
      toast.success(t("marketplace.toasts.updated"));
      setDrawerOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("marketplace.toasts.saveFailed")));
    }
  };

  const archive = async (req) => {
    if (!window.confirm(t("marketplace.actions.confirmArchive"))) return;
    try {
      await api.delete(`/marketplace-requests/${req.id}`);
      toast.success(t("marketplace.toasts.archived"));
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("marketplace.toasts.saveFailed")));
    }
  };

  // A20-P12 — owner-side fetch of vendor offers for one request.
  const loadOffers = useCallback(async (requestId) => {
    setOffersDrawer((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(`/marketplace-requests/${requestId}/offers`);
      setOffersDrawer((s) => ({ ...s, loading: false, data: r.data }));
    } catch (err) {
      setOffersDrawer((s) => ({ ...s, loading: false }));
      toast.error(extractErrorMessage(err, t("marketplace.toasts.offersLoadFailed")));
    }
  }, [t]);

  // A20-P13 — owner picks one visible offer. The select endpoint locks
  // the request (status → offer_selected) and the offer (status →
  // selected). No payment, no vendor contact: admin handles follow-up.
  const selectOffer = useCallback(async (offer) => {
    if (!offersDrawer.requestId) return;
    try {
      const r = await api.post(
        `/marketplace-requests/${offersDrawer.requestId}/offers/${offer.id}/select`,
      );
      setOffersDrawer((s) => ({
        ...s,
        data: s.data
          ? {
              ...s.data,
              request: { ...(s.data.request || {}), ...(r.data?.request || {}) },
              offers: (s.data.offers || []).map((o) =>
                o.id === r.data?.selected_offer?.id
                  ? { ...o, ...r.data.selected_offer }
                  : o,
              ),
            }
          : s.data,
      }));
      toast.success(t("marketplace.offers.toastSelected"));
      // Refresh the list so the card pill reflects the new
      // `offer_selected` status without forcing a page reload.
      fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("marketplace.offers.toastSelectFailed")));
      // On failure (race, expiration, etc.) re-fetch the drawer to show
      // the authoritative server state.
      loadOffers(offersDrawer.requestId);
    }
  }, [offersDrawer.requestId, t, fetchAll, loadOffers]);

  const openOffers = (req) => {
    setOffersDrawer({ open: true, requestId: req.id, loading: true, data: null });
    loadOffers(req.id);
  };

  return (
    <PanelLayout>
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="my-marketplace-requests-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="my-marketplace-title">
              <Inbox className="w-6 h-6 text-fuchsia-700" />
              {t("marketplace.pageTitle")}
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t("marketplace.pageSub")}</p>
            <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {t("marketplace.privacy")}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}
            data-testid="my-marketplace-refresh">
            <RefreshCw className="w-4 h-4 me-1" />
          </Button>
        </div>

        {rows.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-slate-500 text-sm" data-testid="my-marketplace-empty">
                {t("marketplace.empty")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-marketplace-grid">
            {rows.map((req) => (
              <Card key={req.id} className="border-fuchsia-100 hover:border-fuchsia-400 transition" data-testid={`my-marketplace-card-${req.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-semibold text-base text-slate-900 line-clamp-2">{req.title}</h3>
                    <Badge className={`${STATUS_PILL[req.status] || "bg-slate-100"} border-0 text-[10px] uppercase shrink-0`}>
                      {t(`marketplace.status.${req.status}`)}
                    </Badge>
                  </div>
                  {req.description && (
                    <p className="text-xs text-slate-500 line-clamp-2">{req.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-[11px] mt-2">
                    <Badge className="bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-200 text-[10px]" data-testid={`my-marketplace-private-badge-${req.id}`}>
                      <Lock className="w-3 h-3 me-1" />
                      {t("marketplace.privateDraftBadge")}
                    </Badge>
                    <Badge className="bg-slate-50 text-slate-700 border text-[10px]">
                      {t("marketplace.notPublished")}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 pt-1">
                    <span className="tabular-nums">{t("marketplace.card.budget")}: <strong>{fmtEur(req.max_budget)}</strong></span>
                    <span>{t("marketplace.card.quantity")}: <strong>{req.quantity}</strong></span>
                    {req.country && <span>{t("marketplace.card.country")}: <strong>{req.country}</strong></span>}
                  </div>
                  {req.required_by_date && (
                    <div className="text-[11px] text-slate-500">
                      {req.required_by_date}
                    </div>
                  )}
                  <div className="flex justify-end gap-1 pt-1 border-t border-slate-100">
                    {(req.status === "approved_internal" || req.status === "offer_selected") && (
                      <Button size="sm" variant="outline"
                        onClick={() => openOffers(req)}
                        className="me-auto border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                        data-testid={`my-marketplace-view-offers-${req.id}`}>
                        <PackageOpen className="w-3.5 h-3.5 me-1" />
                        {t("marketplace.offers.viewOffers")}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(req)}
                      data-testid={`my-marketplace-edit-${req.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => archive(req)}
                      data-testid={`my-marketplace-archive-${req.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto"
            data-testid="my-marketplace-drawer">
            <SheetHeader>
              <SheetTitle>{t("marketplace.actions.edit")}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-3 text-sm">
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.title")} *</span>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={200} data-testid="my-marketplace-form-title" />
              </label>
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.description")}</span>
                <Textarea rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={2000} data-testid="my-marketplace-form-description" />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <label className="block col-span-1">
                  <span className="block text-slate-600 mb-1">{t("marketplace.card.country")}</span>
                  <select className="w-full border rounded px-2 py-1.5"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    data-testid="my-marketplace-form-country">
                    {PILOT_COUNTRIES.map((c) => (
                      <option key={c} value={c}>{countryLabel(c, t)} ({c})</option>
                    ))}
                  </select>
                </label>
                <label className="block col-span-1">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.city")}</span>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                    maxLength={100} data-testid="my-marketplace-form-city" />
                </label>
                <label className="block col-span-1">
                  <span className="block text-slate-600 mb-1">{t("marketplace.card.quantity")}</span>
                  <Input type="number" min="1" step="1" value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    data-testid="my-marketplace-form-quantity" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("marketplace.card.budget")}</span>
                  <Input type="number" min="0" step="0.01" value={form.max_budget}
                    onChange={(e) => setForm({ ...form, max_budget: e.target.value })}
                    data-testid="my-marketplace-form-budget" />
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.item.requiredBy")}</span>
                  <Input type="date" value={form.required_by_date}
                    onChange={(e) => setForm({ ...form, required_by_date: e.target.value })}
                    data-testid="my-marketplace-form-required" />
                </label>
              </div>
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.status")}</span>
                <select className="w-full border rounded px-2 py-1.5"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  data-testid="my-marketplace-form-status">
                  {P9_STATUSES.map((s) => (
                    <option key={s} value={s}>{t(`marketplace.status.${s}`)}</option>
                  ))}
                </select>
              </label>
              {form.status === "cancelled" && (
                <label className="block">
                  <span className="block text-slate-600 mb-1">Reason *</span>
                  <Textarea rows={2} value={form.cancellation_reason}
                    onChange={(e) => setForm({ ...form, cancellation_reason: e.target.value })}
                    maxLength={500} data-testid="my-marketplace-form-cancelreason" />
                </label>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDrawerOpen(false)}
                  data-testid="my-marketplace-form-cancel">
                  {t("marketplace.actions.cancel")}
                </Button>
                <Button
                  onClick={submit}
                  disabled={!form.title.trim() ||
                    (form.status === "cancelled" && !form.cancellation_reason.trim())}
                  className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
                  data-testid="my-marketplace-form-save"
                >
                  {t("marketplace.actions.save")}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* A20-P12 — Owner offers drawer. Lazy-loaded; no payments, no contact. */}
        <Sheet
          open={offersDrawer.open}
          onOpenChange={(open) =>
            setOffersDrawer((s) => ({ ...s, open, ...(open ? {} : { data: null, requestId: null }) }))
          }
        >
          <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto"
            data-testid="my-marketplace-offers-drawer">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <PackageOpen className="w-5 h-5 text-emerald-700" />
                {t("marketplace.offers.heading")}
              </SheetTitle>
              {offersDrawer.data?.request?.title && (
                <p className="text-sm text-slate-600" data-testid="my-marketplace-offers-drawer-title">
                  {offersDrawer.data.request.title}
                </p>
              )}
            </SheetHeader>
            <div className="mt-4">
              <OfferComparator
                data={offersDrawer.data}
                loading={offersDrawer.loading}
                onRefresh={() => offersDrawer.requestId && loadOffers(offersDrawer.requestId)}
                onSelectOffer={selectOffer}
                testIdPrefix="my-marketplace-offers"
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
    </PanelLayout>
  );
};

export default MyMarketplaceRequests;
